import * as Actions from "../store/actions";
import { PlatformActions, UserInfo, RemoteLogs } from './PlatformActions';
import { WebActions } from './WebActions';
import { getVarsFromWindowUrl } from '../utils/webPage';
import { WebSocketActions, ConnectionInfo } from './WebSocketActions';
import { PlatformEvent, RecordingStatus, ActionRecorderStatusCanCloseVS } from './NativeCommChannel';
import { ActionRecorderState, AttachmentType, FileInfo } from '../store/appState';
import * as AppRiot from "../app";
import { Settings, AssemblyFileNames } from "../Settings";
import { FakePlatformActions } from "./fake/fakePlatformActions";
import { getFileName } from "../utils/files";
import { ResourceStrings } from "../ResourceStrings";
import { hasItems } from "../utils/collections";
import { UrlParam } from "../constants/urlParams";
import { instance } from "../config/serverConfig";

export const RestApiBaseUri =  instance?.sendFeedbackUrl ? instance.sendFeedbackUrl : null;

// export const RestApiBaseUri = "https://localhost:7074/api" as unknown as any; // uncomment for localhost debugging note: the port may be different

export let platformActions: PlatformActions;

export type InitResult = {
    error: string | null;
    mustAuthenticate: boolean;
}

/** Initializes WebSocket (native) or website (regular) communication channel
 * Returns: true if user must authenticate, false if auth will happen through websocket;
 * Return value is used to determine whether to show home page immediately
*/
export function initCommunication(useFakeActions?: boolean): InitResult {
    let error: string | null = null;
    if (RestApiBaseUri == null || RestApiBaseUri === "") {
        error = "ERR_CONFIG_SENDVSFEEDBACK2: Application configuration is incorrect. Cannot determine backend URL (sendvsfeedback2). Is it missing from Azure settings?";
    }

    // Use fake action for testing or debugging just the web UI functionality without need to launch VS etc.
    if (useFakeActions) {
        const params = new URLSearchParams(window.location.search);

        platformActions = new FakePlatformActions(() => {
            AppRiot.App.settings.nativeCapabilities.canReserveNewTrackingId = true;
            return AppRiot.App.settings;
        }, new WebActions(RestApiBaseUri), params.get("fakeProcessConfig"));
        return { error, mustAuthenticate: false };
    }

    const info: ConnectionInfo = getVarsFromWindowUrl([UrlParam.Port, UrlParam.Fsid]) as any;
    if (info.port) {
        platformActions = new WebSocketActions(platformEventHandler, info);
        return { error, mustAuthenticate: false };
    }
    else {
        platformActions = new WebActions(RestApiBaseUri);
        return { error, mustAuthenticate: true };
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function platformEventHandler(event: PlatformEvent, data?: any): void {
    console.log(`Received platform event: ${event.toUpperCase()}`);
    switch (event) {
        case PlatformEvent.disconnected:
            Actions.disconnectedFromNativeProcess(true);
            break;

        case PlatformEvent.collectProcessing: {
            const fileInfos = (data.files as string[]).map((file: string) => {
                return {
                    path: file,
                    name: getFileName(file),
                    fileType: AttachmentType.recording,
                    isUserFile: false
                } as FileInfo;
            });
            Actions.rapAddFilesToUpload(fileInfos);
            Actions.setActionRecorderState(ActionRecorderState.processing, data);
            break;
        }

        case PlatformEvent.collectStatus:
            if (data.code === ActionRecorderStatusCanCloseVS) {
                Actions.setActionRecorderSafeToCloseVS(data);
            }
            break;

        case PlatformEvent.collectDone: {
            const recordingStatus = data.status as RecordingStatus;
            switch (recordingStatus) {
                case RecordingStatus.success:
                    Actions.setActionRecorderState(ActionRecorderState.complete, data);
                    break;
                case RecordingStatus.error:
                    Actions.setActionRecorderState(ActionRecorderState.failed, data);
                    console.error(data);
                    break
                case RecordingStatus.cancel:
                    Actions.setActionRecorderState(ActionRecorderState.cancelled, data);
                    break;
                default: throw Error("Unexpected recording status")
            }
            Actions.rapSetRecordingInProgress(false);
            break;
        }

        case PlatformEvent.clientError: {
            console.error(data);

            let title = data.title ?? ResourceStrings.unexpectedError();
            let message = data.errorMessage;
            let canDismiss = !(data.canDismiss === false);

            if (data.errorMessage && data.errorMessage.indexOf("ERR_HANDSHAKE") !== -1) {
                title = ResourceStrings.disconnected();
                message = ResourceStrings.errorDueToAnotherOpenTab();
                canDismiss = false;
            }

            // workaround for .NET crashing on server timeout (needed for VS 2019 16.7, workaround added in native code for VS 2019 16.8)
            // for more info see:
            // native code workaround: https://devdiv.visualstudio.com/DefaultCollection/DevDiv/_git/Setup-Engine/pullrequest/262193
            // .NET issue (closed in some .net core version): https://github.com/dotnet/runtime/issues/21965  and close PR https://github.com/dotnet/runtime/pull/2281
            if (data.errorMessage && data.errorMessage.indexOf("TaskCanceledException") !== -1) { // if timeout
                // ignore error
                console.log("IGNORING TaskCanceledException");
                return;
            }

            // @TODO: Do more granular reporting of severity once native process supports sending that info
            AppRiot.App.showUnexpectedError(message, canDismiss, true, title);
            break;
        }

        case PlatformEvent.serverSettings:
            // Sent from client itself as part of initial 'Hello' handshake
            AppRiot.App.updateSettings(Object.assign(new Settings(), data as Settings), true);
            // If channel manifest info is available in settings, get release action info
            if (AppRiot.App.channelManifestId !== "unknown") {
                Actions.setReleaseActionInfo(AppRiot.App.channelManifestId, AppRiot.App.clientName);
            }
            break;

        case PlatformEvent.fileCheck:
            console.log(data);
            AppRiot.App.updateSettings(Object.assign(AppRiot.App.settings, data as Settings));
            break;

        case PlatformEvent.signedIn: {
            const user: UserInfo = data;
            AppRiot.App.signInComplete(user, true);
            break;
        }

        case PlatformEvent.signedOut:
            AppRiot.App.signOutComplete();
            break;

        case PlatformEvent.remoteFiles: {
            // legacy code path - no-op since 2025-06-17. Nexus was deprecated years before that.
            // add Nexus remote files to app settings
            const typedData: RemoteLogs = data as RemoteLogs;
            console.log(typedData);
            break;
        }

        default:
            throw new Error("Unknown platform event: " + event);
    }
}
